import React, { useMemo, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// ─── Types ────────────────────────────────────────────────────────────────────

interface EnvelopeChange {
    field: string;
    previousValue: string;
    newValue: string;
}

interface Envelope {
    id: string;
    name: string;
    transactionLabel: string;
    referenceId: string;
    status: string;
    defaultExpanded?: boolean;
    changes: EnvelopeChange[];
}

export interface HumanValidationData {
    warningMessage: string;
    envelopes: Envelope[];
}

interface HumanValidationProps {
    validation?: HumanValidationData;
    locked?: boolean;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const SPACING = {
    betweenCards: 3,
    withinCard: 2,
    betweenEqual: 1,
};

const ACTION_DIALOG_COPY: Record<string, { title: string; message: string; confirmLabel: string }> = {
    promote: {
        title: 'Promote',
        message: 'This action will create or modify setups in the production stage, directly impacting live document traffic. Please review the summary of field modifications carefully before proceeding and click "Accept" only if everything is correct',
        confirmLabel: 'Accept',
    },
    reject: {
        title: 'Reject',
        message: 'This action will delete the setups created in the staging phase, only use this in case you encounter an issue with the summary of field modifications',
        confirmLabel: 'Accept',
    },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getEnvelopeStatusVariant = (status: string): any => {
    if (status === 'New') return 'success';
    return 'informational';
};

const isEmpty = (value: string): boolean => !value || value === '-' || value === '—';

const getChangeStatus = (change: EnvelopeChange): { label: string; color: any } => {
    const prevEmpty = isEmpty(change.previousValue);
    const newEmpty = isEmpty(change.newValue);
    if (prevEmpty && !newEmpty) return { label: 'Addition', color: 'success' };
    if (!prevEmpty && newEmpty) return { label: 'Deletion', color: 'error' };
    if (change.previousValue === change.newValue) return { label: 'No Changes', color: 'passive' };
    return { label: 'Change', color: 'warning' };
};

// ─── Component ────────────────────────────────────────────────────────────────

const HumanValidation: React.FC<HumanValidationProps> = ({ validation, locked = false }) => {
    const [isWarningVisible, setIsWarningVisible] = useState(true);
    const [pendingAction, setPendingAction] = useState<string | null>(null);

    const defaultExpanded = useMemo(() => {
        if (!validation?.envelopes?.length) return false;
        const expandedEnvelope = validation.envelopes.find((e) => e.defaultExpanded);
        return expandedEnvelope ? expandedEnvelope.id : validation.envelopes[0].id;
    }, [validation]);

    const [expandedEnvelope, setExpandedEnvelope] = useState<string | false>(defaultExpanded);

    const handleAccordionChange = (envelopeId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
        setExpandedEnvelope(isExpanded ? envelopeId : false);
    };

    const handleOpenActionDialog = (action: string) => () => setPendingAction(action);
    const handleCloseActionDialog = () => setPendingAction(null);

    const actionDialog = pendingAction ? ACTION_DIALOG_COPY[pendingAction] : null;

    return (
        <Box sx={{ position: 'relative' }}>
            {locked && (
                <Box sx={{
                    position: 'absolute', inset: 0, zIndex: 1,
                    backgroundColor: 'rgba(255,255,255,0.6)',
                    borderRadius: '8px',
                    cursor: 'not-allowed',
                }} />
            )}
            <Accordion
                defaultExpanded={!locked}
                sx={{
                    borderRadius: '8px !important',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.1) !important',
                    border: 'none',
                    '&:before': { display: 'none' },
                    '&.MuiAccordion-root': { border: 'none' },
                }}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                        <Typography variant="h2" sx={{ color: 'rgba(0,0,0,0.87)' }}>
                            Human Validation - Promote to Production
                            <span style={{ color: '#d32f2f', marginLeft: '4px', fontSize: 'inherit', fontWeight: 700 }}>*</span>
                        </Typography>
                        {locked && !validation && (
                            <Typography variant="bodySmall" color="text.secondary">
                                Complete the Test stage to unlock human validation.
                            </Typography>
                        )}
                    </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: validation ? SPACING.betweenCards : 0 }}>
                    {validation ? (
                        <Paper sx={{ p: SPACING.withinCard, borderRadius: '8px', border: '1px solid #e0e0e0' }}>
                            {isWarningVisible && (
                                <Alert
                                    severity="warning"
                                    icon={<InfoOutlinedIcon fontSize="inherit" />}
                                    action={(
                                        <IconButton
                                            aria-label="close warning"
                                            color="inherit"
                                            size="small"
                                            onClick={() => setIsWarningVisible(false)}
                                        >
                                            <CloseIcon fontSize="inherit" />
                                        </IconButton>
                                    )}
                                    sx={{
                                        mb: SPACING.withinCard,
                                        alignItems: 'center',
                                        backgroundColor: '#ffd5a5',
                                        color: '#3f2a0b',
                                    }}
                                >
                                    <Typography variant="bodyMedium" sx={{ fontWeight: 700 }}>
                                        {validation.warningMessage}
                                    </Typography>
                                </Alert>
                            )}

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: SPACING.withinCard, mb: SPACING.withinCard, flexWrap: 'wrap' }}>
                                <Typography variant="bodyMedium">Envelope Change Review</Typography>
                                <Typography variant="bodySmall" color="text.secondary">
                                    {validation.envelopes.length} envelopes to review
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: SPACING.withinCard }}>
                                {validation.envelopes.map((envelope) => (
                                    <Accordion
                                        key={envelope.id}
                                        expanded={expandedEnvelope === envelope.id}
                                        onChange={handleAccordionChange(envelope.id)}
                                        disableGutters
                                        sx={{
                                            border: '1px solid #d2d6db',
                                            borderRadius: '8px !important',
                                            boxShadow: 'none',
                                            '&:before': { display: 'none' },
                                            overflow: 'hidden',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                px: SPACING.withinCard,
                                                py: 0.5,
                                                '& .MuiAccordionSummary-content': { my: 1, alignItems: 'center' },
                                            }}
                                        >
                                            <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: SPACING.withinCard, flexWrap: 'wrap' }}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <Typography variant="labelLarge" component="div" sx={{ display: 'block' }}>
                                                        {envelope.name}
                                                    </Typography>
                                                    <Typography variant="labelSmall" component="div" color="text.secondary" sx={{ display: 'block' }}>
                                                        {envelope.transactionLabel} · ID: {envelope.referenceId}
                                                    </Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: SPACING.withinCard, ml: 'auto' }}>
                                                    <Chip
                                                        label={envelope.status}
                                                        size="small"
                                                        color={getEnvelopeStatusVariant(envelope.status)}
                                                        variant="badge"
                                                    />
                                                    <Typography variant="bodyMedium" color="text.secondary">
                                                        {envelope.changes.length} Changes
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </AccordionSummary>

                                        <AccordionDetails sx={{ px: 0, py: 0, backgroundColor: '#fcfcfc' }}>
                                            <Box sx={{ px: SPACING.withinCard, py: SPACING.withinCard }}>
                                                <Box sx={{ display: 'flex', pb: 1.5, color: '#1f1f1f', fontWeight: 700 }}>
                                                    <Box sx={{ flexBasis: '34%', maxWidth: '34%' }}>
                                                        <Typography variant="labelMedium">&nbsp;</Typography>
                                                    </Box>
                                                    <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                        <Typography variant="labelMedium">Previous Value</Typography>
                                                    </Box>
                                                    <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                        <Typography variant="labelMedium">New Value</Typography>
                                                    </Box>
                                                    <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                        <Typography variant="labelMedium">Changes</Typography>
                                                    </Box>
                                                </Box>
                                                <Divider />
                                                {envelope.changes.map((change) => {
                                                    const status = getChangeStatus(change);
                                                    return (
                                                        <React.Fragment key={`${envelope.id}-${change.field}`}>
                                                            <Box sx={{ display: 'flex', py: 1.5, alignItems: 'center' }}>
                                                                <Box sx={{ flexBasis: '34%', maxWidth: '34%' }}>
                                                                    <Typography variant="bodyMedium">{change.field}</Typography>
                                                                </Box>
                                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                    <Typography variant="bodyMedium">
                                                                        {isEmpty(change.previousValue) ? '—' : change.previousValue}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                    <Typography variant="bodyMedium">
                                                                        {isEmpty(change.newValue) ? '—' : change.newValue}
                                                                    </Typography>
                                                                </Box>
                                                                <Box sx={{ flexBasis: '22%', maxWidth: '22%' }}>
                                                                    <Chip
                                                                        label={status.label}
                                                                        size="small"
                                                                        variant="badge"
                                                                        color={status.color}
                                                                    />
                                                                </Box>
                                                            </Box>
                                                            <Divider />
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: SPACING.betweenEqual, mt: SPACING.betweenCards, flexWrap: 'wrap' }}>
                                <Button variant="primary" onClick={handleOpenActionDialog('promote')}>
                                    Promote to Production
                                </Button>
                                <Button variant="secondary" onClick={handleOpenActionDialog('reject')}>
                                    Reject & Rollback
                                </Button>
                            </Box>
                        </Paper>
                    ) : null}
                </AccordionDetails>
            </Accordion>

            <Dialog
                open={Boolean(actionDialog)}
                onClose={handleCloseActionDialog}
                aria-labelledby="human-validation-action-title"
                aria-describedby="human-validation-action-description"
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle id="human-validation-action-title" sx={{ px: 3, pt: 3, pb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <InfoOutlinedIcon sx={{ color: '#000000', fontSize: '1.5rem' }} />
                        <Typography variant="h2">{actionDialog?.title}</Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ px: 3, py: 2 }}>
                    <DialogContentText id="human-validation-action-description">
                        {actionDialog?.message}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button variant="secondary" onClick={handleCloseActionDialog}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleCloseActionDialog}>
                        {actionDialog?.confirmLabel}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default HumanValidation;
